<template>
    <div class="automation-debug" v-animate-css="{ classes: 'slideInUp', delay: 0, duration: 300 }">
        <section class="header">
            <span class="material-icons text-info" style="font-size: 30px">terminal</span>
            <div class="d-flex flex-column">
                <strong class="font-weight-bold" style="font-size: 18px; letter-spacing: 1.2px"
                    >Automation Test Tool</strong
                >
                <small class="text-success">
                    <a-badge color="green" />
                    Automation Debugger Active
                </small>
            </div>
            <div>
                <md-button class="md-icon-button" @click="closeWidget">
                    <md-icon>close</md-icon>
                </md-button>
            </div>
        </section>
        <section class="steps">
            <TaskStepViewer :value="step" v-for="step in steps" :key="step.order" />
        </section>
        <section class="footer">
            <SupsisButton ghost @click="continueTest" :disabled="closed">
                <i class="material-icons">play_arrow</i>
                Continue Test
                <div class="key">
                    <span data-v-63a309c6="" class="badge badge-secondary">CMD</span>
                    +
                    <span data-v-63a309c6="" class="badge badge-secondary">→</span>
                </div>
            </SupsisButton>

            <SupsisButton ghost @click="startTest" v-if="closed">
                <i class="material-icons">play_arrow</i>
                Start new Test
                <div class="key">
                    <span data-v-63a309c6="" class="badge badge-secondary">CMD</span>
                    +
                    <span data-v-63a309c6="" class="badge badge-secondary">ENTER</span>
                </div>
            </SupsisButton>
        </section>
    </div>
</template>

<script>
import { sendSocketMessage, socketMessageStream } from "../../../../../../utils/socketStream";
import { generateUUID } from "../../../../../../utils/session";
import _ from "lodash";
import log2 from "../../../../../../utils/log2";
import { filter } from "rxjs/operators";
import { mapActions, mapMutations, mapState } from "vuex";
import SYSTEM_PUBSUB from "../../../../../../utils/systemPubSub";
import TaskStepViewer from "./TaskStepViewer.vue";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";

const log = log2("AutomationDebugger");
export default {
    name: "AutomationDebugger",
    props: ["diagram", "testStartBlockId"],
    components: { SupsisButton, TaskStepViewer },
    data() {
        return {
            currentBlockId: this.testStartBlockId,
            steps: [],
            state: {},
            testId: null,
            closed: true,
            cancels: [],
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => _.get(state.site.site, "_id"),
            automation: (state) => state.automation.current,
            automationTestState: (state) => state.automation.testState,
        }),
    },
    mounted() {
        log("mounted");
        this.subscribeSocketEvents();
        this.startTest();
        const keydownCallback = (e) => {
            //if ctrl + right arrow
            console.log("keydownCallback", e, e.ctrlKey || e.metaKey, e.keyCode);
            if ((e.ctrlKey || e.metaKey) && e.keyCode === 39) {
                if (!this.closed) this.continueTest();
            }

            if ((e.ctrlKey || e.metaKey) && e.keyCode === 13) {
                if (this.closed) this.startTest();
            }
        };
        window.addEventListener("keydown", keydownCallback);
        this.cancels.push(() => {
            window.removeEventListener("keydown", keydownCallback);
        });
    },
    methods: {
        ...mapMutations({}),
        ...mapActions({
            changeTestSate: "automation/changeTestSate",
        }),
        continueTest() {
            sendSocketMessage("automation continue-test", {
                siteId: this.siteId,
                testId: this.testId,
                automationId: this.automation._id,
            });
        },
        startTest() {
            this.changeTestSate({
                ...this.automationTestState,
                active: true,
                startBlockId: this.automation.diagram.id,
                position: this.automation.diagram.id,
                history: [],
            });
            this.steps = [];
            this.state = {};
            this.testId = "automation-test-" + generateUUID();
            this.closed = false;
            // this.setConversationId(this.testId);
            log("startTest", this.testId);
            sendSocketMessage("automation start-test", {
                siteId: this.siteId,
                startBlockId: this.testStartBlockId,
                testId: this.testId,
                automationId: this.automation._id,
            });
        },
        closeDebugger() {
            sendSocketMessage("automation close-test", {
                siteId: this.siteId,
                testId: this.testId,
            });
        },
        resetDebugger() {
            if (this.testId) {
                this.closeDebugger(this.testId);
            }
            this.steps = [];
            this.state = {};
            this.startTest();
        },
        subscribeSocketEvents() {
            this.socketEventSubscriber = socketMessageStream
                .pipe(filter((x) => x.cmd.startsWith("automation")))
                .subscribe(({ cmd, data }) => {
                    log("automation event", cmd, data);
                    if (cmd === "automation state_changed") {
                        this.state = data.state;
                        this.steps = data.steps;

                        this.changeTestSate({
                            ...this.automationTestState,
                            active: true,
                            history: data.steps.map((step) => step.blockId),
                            position: data.steps[data.steps.length - 1]?.blockId,
                        });
                    } else if (cmd === "automation task_terminated") {
                        this.handleClose(data.testId);
                    }
                });
        },
        handleClose() {
            this.closed = true;
            this.changeTestSate({
                ...this.automationTestState,
                active: false,
                history: [],
                startBlockId: null,
                position: null,
            });
        },

        closeWidget() {
            if (this.testId) this.closeDebugger(this.testId);
            this.handleClose();
            this.$emit("close-menu", 0);
        },
    },
    beforeDestroy() {
        this.socketEventSubscriber.unsubscribe();
        SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_CHAT_WIDGET_OPEN_STATE_CHANGED, {
            visible: false,
        });
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        testStartBlockId(newBlockId) {
            this.resetDebugger();
        },
        diagram: {
            handler(newDiagram) {
                this.resetDebugger();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.automation-debug {
    border-radius: 15px;
    box-shadow: 0 0 15px rgb(0 0 0 / 10%);
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    height: calc(100vh - 150px);
    max-height: 100%;
    max-width: 100%;
    overflow: hidden;
    pointer-events: all;
    position: relative;
    user-select: text;
    width: 550px;
    z-index: 999;

    .header {
        align-items: center;
        border-radius: 15px 15px 0 0;
        display: flex;
        height: 100px;
        padding: 0 24px;
        justify-content: space-between;
        background: white;
    }

    .steps {
        flex-grow: 1;

        position: relative;
        background: rgb(234, 238, 243);
        max-height: calc(100% - 160px);
        overflow: scroll;
    }

    section.footer {
        border-top-color: rgb(234, 234, 234);
        background: rgb(255, 255, 255);
        align-items: center;
        justify-content: center;
        border-radius: 0 0 15px 15px;
        display: flex;
        width: 100%;
        height: 60px;

        .key {
            .bagde {
                display: inline-block;
                padding: 0.25em 0.4em;
                font-size: 75%;
                font-weight: 700;
                line-height: 1;
                text-align: center;
                white-space: nowrap;
                vertical-align: baseline;
                border-radius: 0.25rem;

                transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out,
                    box-shadow 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
            }

            .badge-secondary {
                color: #fff;
                background-color: #6c757d;
            }
        }
    }
}
</style>
